import { Suspense } from "react";
import { lazyRetry } from "@/lib/lazyRetry";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import TeamRedirect from "./components/team/TeamRedirect";
import { AuthProvider } from "@/hooks/useAuth";
import { ThemeProvider } from "@/hooks/useTheme";
import { Layout } from "@/components/Layout";
import { ScrollToTop } from "@/components/ScrollToTop";
import { ReferralCapture } from "@/components/ReferralCapture";
import { RouteFallback } from "@/components/RouteFallback";
import { PWAUpdatePrompt } from "@/components/PWAUpdatePrompt";
import { HelmetProvider } from "react-helmet-async";
import { handleQueryError, handleMutationError } from "@/lib/observability";

// Eager-loaded routes (critical path)
import Index from "./pages/Index";
import Auth from "./pages/Auth";
import NotFound from "./pages/NotFound";

// Lazy-loaded routes (heavy pages)
const Dashboard = lazyRetry(() => import("./pages/Dashboard"));
const UserProfile = lazyRetry(() => import("./pages/UserProfile"));
const Tournaments = lazyRetry(() => import("./pages/Tournaments"));
const PublicTournaments = lazyRetry(() => import("./pages/PublicTournaments"));
const TournamentDetails = lazyRetry(() => import("./pages/TournamentDetails"));
const TournamentRegistration = lazyRetry(() => import("./pages/TournamentRegistration"));
const TournamentRegistrationNoAuth = lazyRetry(() => import("./pages/TournamentRegistrationNoAuth"));
const TournamentRegistrationsManagement = lazyRetry(() => import("./pages/TournamentRegistrationsManagement"));
const CreateTournament = lazyRetry(() => import("./pages/CreateTournament"));
const TournamentCreateMode = lazyRetry(() => import("./pages/TournamentCreateMode"));
const QuickScoreEntry = lazyRetry(() => import("./pages/QuickScoreEntry"));
const EditTournament = lazyRetry(() => import("./pages/EditTournament"));
const TournamentManagement = lazyRetry(() => import("./pages/TournamentManagement"));
const Teams = lazyRetry(() => import("./pages/Teams"));
const AthletesSearch = lazyRetry(() => import("./pages/AthletesSearch"));
const Explore = lazyRetry(() => import("./pages/Explore"));
const CreateTeam = lazyRetry(() => import("./pages/CreateTeam"));
const MyRegistrations = lazyRetry(() => import("./pages/MyRegistrations"));
const Favorites = lazyRetry(() => import("./pages/Favorites"));
// MatchManagement removed — redirected to Central de Jogos (TournamentBracket)
const PublicMatches = lazyRetry(() => import("./pages/PublicMatches"));
const OrganizerDashboard = lazyRetry(() => import("./pages/OrganizerDashboard"));
const OrganizerReceivables = lazyRetry(() => import("./pages/OrganizerReceivables"));
const ExpenseManagement = lazyRetry(() => import("./pages/ExpenseManagement"));
const BudgetTemplatesAdmin = lazyRetry(() => import("./pages/BudgetTemplatesAdmin"));
const PaymentSuccess = lazyRetry(() => import("./pages/PaymentSuccess"));
const TournamentBracket = lazyRetry(() => import("./pages/TournamentBracket"));
// EditBracket removed — redirected to Central de Jogos (TournamentBracket)
const GroupStandingsPage = lazyRetry(() => import("./pages/GroupStandingsPage"));
const StandingsAuditPage = lazyRetry(() => import("./pages/StandingsAuditPage"));
const TournamentPreview = lazyRetry(() => import("./pages/TournamentPreview"));
const TeamTransferHistory = lazyRetry(() => import("./pages/TeamTransferHistory"));
const BuscaDuplas = lazyRetry(() => import("./pages/BuscaDuplas"));
const CompleteSharedRegistration = lazyRetry(() => import("./pages/CompleteSharedRegistration"));
const GlobalRanking = lazyRetry(() => import("./pages/GlobalRanking"));
const ParaOrganizadores = lazyRetry(() => import("./pages/ParaOrganizadores"));
const Funcionalidades = lazyRetry(() => import("./pages/Funcionalidades"));
const TermosDeUso = lazyRetry(() => import("./pages/TermosDeUso"));
const PoliticaDeReembolso = lazyRetry(() => import("./pages/PoliticaDeReembolso"));
const PoliticaDePrivacidade = lazyRetry(() => import("./pages/PoliticaDePrivacidade"));
const RankingConfiguration = lazyRetry(() => import("./components/RankingConfiguration"));
const TeamProfile = lazyRetry(() => import("./pages/TeamProfile"));
const EditTeamProfile = lazyRetry(() => import("./pages/EditTeamProfile"));
const AdminPanel = lazyRetry(() => import("./pages/AdminPanel"));
const SelectRole = lazyRetry(() => import("./pages/SelectRole"));
const OrganizerPlanSelect = lazyRetry(() => import("./pages/OrganizerPlanSelect"));
const VerifyDevice = lazyRetry(() => import("./pages/VerifyDevice"));
const AdminClassModule = lazyRetry(() => import("./pages/AdminClassModule"));
const CommunityReportsAdmin = lazyRetry(() => import("./pages/admin/CommunityReportsAdmin"));
const CreateArena = lazyRetry(() => import("./pages/CreateArena"));
const OrganizerCompanySignup = lazyRetry(() => import("./pages/OrganizerCompanySignup"));
const MinhasArenas = lazyRetry(() => import("./pages/MinhasArenas"));
const DetalheSessao = lazyRetry(() => import("./pages/DetalheSessao"));
const CadastroTurma = lazyRetry(() => import("./pages/CadastroTurma"));
const DetalheTurma = lazyRetry(() => import("./pages/DetalheTurma"));
const GestaoAlunosTurma = lazyRetry(() => import("./pages/GestaoAlunosTurma"));
const EditarTurma = lazyRetry(() => import("./pages/EditarTurma"));
const MercadoPagoCheckoutPage = lazyRetry(() => import("./pages/MercadoPagoCheckoutPage"));
const RegistrationConfirmation = lazyRetry(() => import("./pages/RegistrationConfirmation"));
const ClassCheckin = lazyRetry(() => import("./pages/ClassCheckin"));
const SolucaoTorneios = lazyRetry(() => import("./pages/SolucaoTorneios"));
const SolucaoERP = lazyRetry(() => import("./pages/SolucaoERP"));
const SolucaoAtleta = lazyRetry(() => import("./pages/SolucaoAtleta"));
const SolucaoArenas = lazyRetry(() => import("./pages/SolucaoArenas"));
const GestaoDeArenas = lazyRetry(() => import("./pages/GestaoDeArenas"));
const AthleteProfile = lazyRetry(() => import("./pages/AthleteProfile"));
const EditAthleteProfile = lazyRetry(() => import("./pages/EditAthleteProfile"));
const RedefinirSenha = lazyRetry(() => import("./pages/RedefinirSenha"));
const PublicBooking = lazyRetry(() => import("./pages/PublicBooking"));
const ArenaUniformes = lazyRetry(() => import("./pages/arena/ArenaUniformes"));
const UniformPresale = lazyRetry(() => import("./pages/UniformPresale"));
const UniformOrderStatus = lazyRetry(() => import("./pages/UniformOrderStatus"));
const PWAInstall = lazyRetry(() => import("./pages/PWAInstall"));
const MobileClassCheckin = lazyRetry(() => import("./pages/MobileClassCheckin"));
const AcceptTournamentManager = lazyRetry(() => import("./pages/AcceptTournamentManager"));
const HandleResolver = lazyRetry(() => import("./components/handle/HandleResolver"));
const AffiliatePortal = lazyRetry(() => import("./pages/AffiliatePortal"));
const CommercialPortal = lazyRetry(() => import("./pages/CommercialPortal"));
const CheckoutComercial = lazyRetry(() => import("./pages/CheckoutComercial"));
const OAuthConsent = lazyRetry(() => import("./pages/OAuthConsent"));
const ToolsHub = lazyRetry(() => import("./pages/tools/ToolsHub"));
const PairDrawTool = lazyRetry(() => import("./pages/tools/PairDrawTool"));
const BracketTool = lazyRetry(() => import("./pages/tools/BracketTool"));
const PublicDrawLive = lazyRetry(() => import("./pages/tools/PublicDrawLive"));


// New Arena Layout and Pages
const ArenaLayout = lazyRetry(() => import("./components/layouts/ArenaLayout").then(m => ({ default: m.ArenaLayout })));
const ArenaDashboardNew = lazyRetry(() => import("./pages/arena/ArenaDashboardNew"));
const ArenaAgendaNew = lazyRetry(() => import("./pages/arena/ArenaAgendaNew"));

const ArenaAlunos = lazyRetry(() => import("./pages/arena/ArenaAlunos"));
const ArenaFinanceiro = lazyRetry(() => import("./pages/arena/ArenaFinanceiro"));
const ArenaConfig = lazyRetry(() => import("./pages/arena/ArenaConfig"));
const ArenaEquipe = lazyRetry(() => import("./pages/arena/ArenaEquipe"));
const AcceptInvite = lazyRetry(() => import("./pages/arena/AcceptInvite"));
const ArenaTurmas = lazyRetry(() => import("./pages/arena/ArenaTurmas"));
const ArenaTorneios = lazyRetry(() => import("./pages/arena/ArenaTorneios"));
const ArenaPlanSelect = lazyRetry(() => import("./pages/arena/ArenaPlanSelect"));
const ArenaAccessControl = lazyRetry(() => import("./pages/arena/ArenaAccessControl"));

// Athlete Portal
const AthleteLayout = lazyRetry(() => import("./components/layouts/AthleteLayout"));
const AthleteHome = lazyRetry(() => import("./pages/athlete/AthleteHome"));
const AthleteReservas = lazyRetry(() => import("./pages/athlete/AthleteReservas"));
const AthleteAulas = lazyRetry(() => import("./pages/athlete/AthleteAulas"));
const AthleteJogos = lazyRetry(() => import("./pages/athlete/AthleteJogos"));
const AthleteFinanceiro = lazyRetry(() => import("./pages/athlete/AthleteFinanceiro"));
const AthleteNotificacoes = lazyRetry(() => import("./pages/athlete/AthleteNotificacoes"));
const AthleteRankingHub = lazyRetry(() => import("./pages/athlete/AthleteRankingHub"));

// Arena Sub-Pages (rendered inside ArenaLayout via Outlet)
const ArenaQuadras = lazyRetry(() => import("./pages/ArenaQuadras"));
const ArenaPrecos = lazyRetry(() => import("./pages/ArenaPrecos"));
const ArenaMensalistas = lazyRetry(() => import("./pages/ArenaMensalistas"));
const ArenaBar = lazyRetry(() => import("./pages/ArenaBar"));
const ArenaPDV = lazyRetry(() => import("./pages/arena/ArenaPDV"));
const ArenaAdquirentes = lazyRetry(() => import("./pages/arena/ArenaAdquirentes"));
const ArenaConciliacao = lazyRetry(() => import("./pages/arena/ArenaConciliacao"));
const ArenaTerminais = lazyRetry(() => import("./pages/arena/ArenaTerminais"));
const ArenaIdentityClaims = lazyRetry(() => import("./pages/arena/ArenaIdentityClaims"));
const ArenaClienteFicha = lazyRetry(() => import("./pages/arena/ArenaClienteFicha"));
const ArenaOperacao = lazyRetry(() => import("./pages/arena/ArenaOperacao"));
const ArenaNotificacoes = lazyRetry(() => import("./pages/arena/ArenaNotificacoes"));
const ArenaContratos = lazyRetry(() => import("./pages/arena/ArenaContratos"));
const AdminIdentityDisputes = lazyRetry(() => import("./pages/admin/AdminIdentityDisputes"));
const AccountIdentity = lazyRetry(() => import("./pages/account/AccountIdentity"));

import { QueryCache, MutationCache } from "@tanstack/react-query";

const queryClient = new QueryClient({
  queryCache: new QueryCache({
    onError: (error, query) => handleQueryError(error, query),
  }),
  mutationCache: new MutationCache({
    onError: (error, variables, context, mutation) => handleMutationError(error, variables, context, mutation),
  }),
  defaultOptions: {
    queries: {
      retry: (failureCount, error: any) => {
        if (error?.code === '42501' || error?.code === 'PGRST301' || error?.status === 401 || error?.status === 403) return false;
        return failureCount < 2;
      },
      // staleTime moderado: dados frescos por 30s → reduz refetches em navegação rápida
      staleTime: 30_000,
      // gcTime maior (5min): mantém cache disponível para navegação reversa sem refetch
      gcTime: 5 * 60_000,
      refetchOnWindowFocus: false,
      // Reconectar não força refetch; observability captura erros reais
      refetchOnReconnect: 'always',
      refetchOnMount: true,
      // Perf: nao rodar polling em abas ocultas (economia de CPU/rede/Cloud)
      refetchIntervalInBackground: false,
    },
    mutations: {
      retry: false,
    },
  },
});

// Suspense fallback component — leve, mantém o shell (Header/Footer/Nav) visível
function PageLoader() {
  return <RouteFallback />;
}

const App = () => (
  <HelmetProvider>
  <QueryClientProvider client={queryClient}>
    <ThemeProvider>
      <TooltipProvider>
        <Toaster />
        <Sonner />
        <PWAUpdatePrompt />
        <BrowserRouter>
          <ScrollToTop />
          <ReferralCapture />
          <AuthProvider>
            <Layout>
              <Suspense fallback={<PageLoader />}>
                <Routes>
                  <Route path="/" element={<Index />} />
                  <Route path="/autenticacao" element={<Auth />} />
                  <Route path="/redefinir-senha" element={<RedefinirSenha />} />
                  <Route path="/painel" element={<Dashboard />} />
                  <Route path="/perfil" element={<UserProfile />} />
                  <Route path="/torneios" element={<Tournaments />} />
                  <Route path="/torneios-publicos" element={<PublicTournaments />} />
                  <Route path="/torneios/:id" element={<TournamentDetails />} />
                  <Route path="/torneios/:id/inscrever" element={<TournamentRegistration />} />
                  <Route path="/torneios/:id/inscrever-sem-login" element={<TournamentRegistrationNoAuth />} />
                  <Route path="/torneios/:id/inscricoes" element={<TournamentRegistrationsManagement />} />
                  <Route path="/torneios/novo" element={<TournamentCreateMode />} />
                  <Route path="/torneios/criar" element={<CreateTournament />} />
                  <Route path="/torneios/:id/editar" element={<EditTournament />} />
                  <Route path="/torneios/:id/gerenciar" element={<TournamentManagement />} />
                  {/* Rotas canônicas /times/* (padrão ge.globo/ESPN) */}
                  <Route path="/times" element={<Teams />} />
                  <Route path="/times/criar" element={<CreateTeam />} />
                  <Route path="/times/:slugOrId" element={<TeamProfile />} />
                  <Route path="/times/:slugOrId/editar" element={<EditTeamProfile />} />
                  {/* Compat: /equipes/* redireciona para /times/* sem quebrar links antigos */}
                  <Route path="/equipes" element={<Navigate to="/times" replace />} />
                  <Route path="/equipes/criar" element={<Navigate to="/times/criar" replace />} />
                  <Route path="/equipes/:slugOrId" element={<TeamRedirect />} />
                  <Route path="/equipes/:slugOrId/editar" element={<TeamRedirect editar />} />
                  <Route path="/minhas-inscricoes" element={<MyRegistrations />} />
                  <Route path="/historico-trocas" element={<TeamTransferHistory />} />
                  <Route path="/busca-duplas" element={<BuscaDuplas />} />
                  <Route path="/completar-inscricao/:linkId" element={<CompleteSharedRegistration />} />
                  <Route path="/pagamento-sucesso" element={<PaymentSuccess />} />
                  <Route path="/favoritos" element={<Favorites />} />
                  {/* MatchManagement redirects to Central de Jogos */}
                  <Route path="/torneios/:id/jogos" element={<TournamentBracket />} />
                  <Route path="/torneios/:id/jogos-publicos" element={<PublicMatches />} />
                  <Route path="/torneios/:id/placar-rapido" element={<QuickScoreEntry />} />
                  <Route path="/painel-organizador" element={<OrganizerDashboard />} />
                  <Route path="/organizador/recebimentos" element={<OrganizerReceivables />} />
                  <Route path="/plano-organizador" element={<OrganizerPlanSelect />} />
                  <Route path="/torneios/:tournamentId/despesas" element={<ExpenseManagement />} />
                  <Route path="/admin" element={<AdminPanel />} />
                  <Route path="/admin/modelos-orcamento" element={<BudgetTemplatesAdmin />} />
                  <Route path="/admin/modulo-aulas" element={<AdminClassModule />} />
                  <Route path="/admin/identity-disputes" element={<AdminIdentityDisputes />} />
                  <Route path="/admin/community/reports" element={<CommunityReportsAdmin />} />
                  <Route path="/conta/identidade" element={<AccountIdentity />} />
                  <Route path="/criar-arena" element={<CreateArena />} />
                  <Route path="/cadastro-organizador" element={<OrganizerCompanySignup />} />
                  <Route path="/minhas-arenas" element={<MinhasArenas />} />
                  
                  {/* Arena Routes with Sidebar Layout */}
                  <Route path="/arena/:arenaId" element={<ArenaLayout />}>
                    <Route index element={<ArenaDashboardNew />} />
                    <Route path="agenda" element={<ArenaAgendaNew />} />
                    <Route path="reservas" element={<ArenaAgendaNew />} />
                    <Route path="alunos" element={<ArenaAlunos />} />
                    <Route path="financeiro" element={<ArenaFinanceiro />} />
                    <Route path="config" element={<ArenaConfig />} />
                    <Route path="quadras" element={<ArenaQuadras />} />
                    <Route path="precos" element={<ArenaPrecos />} />
                    <Route path="mensalistas" element={<ArenaMensalistas />} />
                    <Route path="bar" element={<ArenaBar />} />
                    <Route path="pdv" element={<ArenaPDV />} />
                    <Route path="adquirentes" element={<ArenaAdquirentes />} />
                    <Route path="conciliacao" element={<ArenaConciliacao />} />
                    <Route path="terminais" element={<ArenaTerminais />} />
                    <Route path="turmas" element={<ArenaTurmas />} />
                    <Route path="torneios" element={<ArenaTorneios />} />
                    <Route path="plano" element={<ArenaPlanSelect />} />
                    <Route path="acesso" element={<ArenaAccessControl />} />
                    <Route path="turma/nova" element={<CadastroTurma />} />
                    <Route path="turma/:turmaId" element={<DetalheTurma />} />
                    <Route path="turma/:turmaId/alunos" element={<GestaoAlunosTurma />} />
                    <Route path="turma/:turmaId/editar" element={<EditarTurma />} />
                    <Route path="sessao/:sessaoId" element={<DetalheSessao />} />
                    <Route path="equipe" element={<ArenaEquipe />} />
                    <Route path="identidade-reivindicacoes" element={<ArenaIdentityClaims />} />
                    <Route path="cliente/:personId" element={<ArenaClienteFicha />} />
                    <Route path="operacao" element={<ArenaOperacao />} />
                    <Route path="notificacoes" element={<ArenaNotificacoes />} />
                    <Route path="contratos" element={<ArenaContratos />} />
                    <Route path="uniformes" element={<ArenaUniformes />} />
                  </Route>
                  
                  {/* Accept arena invite */}
                  <Route path="/aceitar-convite" element={<AcceptInvite />} />
                  
                  {/* Athlete Portal */}
                  <Route path="/atleta" element={<AthleteLayout />}>
                    <Route index element={<AthleteHome />} />
                    <Route path="reservas" element={<AthleteReservas />} />
                    <Route path="aulas" element={<AthleteAulas />} />
                    <Route path="jogos" element={<AthleteJogos />} />
                    <Route path="inscricoes" element={<MyRegistrations />} />
                    <Route path="financeiro" element={<AthleteFinanceiro />} />
                    <Route path="notificacoes" element={<AthleteNotificacoes />} />
                    <Route path="ranking" element={<AthleteRankingHub />} />
                    <Route path="historico" element={<AthleteProfile />} />
                    <Route path="perfil" element={<AthleteProfile />} />
                    <Route path="times" element={<Teams />} />
                    <Route path="times/criar" element={<CreateTeam />} />
                  </Route>
                  <Route path="/ranking-geral" element={<GlobalRanking />} />
                  {/* Busca pública de atletas */}
                  <Route path="/atletas" element={<AthletesSearch />} />
                  <Route path="/explorar" element={<Explore />} />
                  <Route path="/atleta/:athleteId" element={<AthleteProfile />} />
                  <Route path="/meu-perfil-atleta" element={<EditAthleteProfile />} />
                  <Route path="/admin/configuracao-ranking" element={<RankingConfiguration />} />
                  <Route path="/torneios/:id/chaves" element={<TournamentBracket />} />
                  {/* EditBracket redirects to Central de Jogos */}
                  <Route path="/torneios/:id/editar-chaves" element={<TournamentBracket />} />
                  <Route path="/torneios/:id/classificacao" element={<GroupStandingsPage />} />
                  <Route path="/torneios/:id/auditoria" element={<StandingsAuditPage />} />
                  <Route path="/torneios/:id/preview" element={<TournamentPreview />} />
                  <Route path="/para-organizadores" element={<ParaOrganizadores />} />
                  <Route path="/funcionalidades" element={<Funcionalidades />} />
                  <Route path="/termos-de-uso" element={<TermosDeUso />} />
                  <Route path="/politica-de-reembolso" element={<PoliticaDeReembolso />} />
                  <Route path="/politica-de-privacidade" element={<PoliticaDePrivacidade />} />
                  <Route path="/selecionar-perfil" element={<SelectRole />} />
                  <Route path="/verify-device" element={<VerifyDevice />} />
                  <Route path="/solucao/torneios" element={<SolucaoTorneios />} />
                  <Route path="/solucao/erp-financeiro" element={<SolucaoERP />} />
                  <Route path="/solucao/portal-atleta" element={<SolucaoAtleta />} />
                  <Route path="/solucao/arenas" element={<SolucaoArenas />} />
                  <Route path="/gestao-de-arenas" element={<GestaoDeArenas />} />
                  <Route path="/checkout/:registrationId" element={<MercadoPagoCheckoutPage />} />
                  <Route path="/inscricao-confirmacao/:registrationId" element={<RegistrationConfirmation />} />
                  <Route path="/inscricao-confirmacao/link/:linkId" element={<RegistrationConfirmation />} />
                  <Route path="/checkin/:encoded" element={<ClassCheckin />} />
                  {/* Public booking page */}
                  <Route path="/reservar/:slug" element={<PublicBooking />} />
                  {/* Pré-venda pública de uniformes da arena */}
                  <Route path="/uniformes/pedido/:token" element={<UniformOrderStatus />} />
                  <Route path="/uniformes/:arenaSlug/:campaignSlug" element={<UniformPresale />} />
                  <Route path="/instalar" element={<PWAInstall />} />
                  <Route path="/arena/:arenaSlug/checkin-aulas" element={<MobileClassCheckin />} />
                  <Route path="/aceitar-co-gestor" element={<AcceptTournamentManager />} />
                  <Route path="/comercial" element={<CommercialPortal />} />
                  <Route path="/afiliado" element={<AffiliatePortal />} />
                  <Route path="/afiliado/*" element={<AffiliatePortal />} />
                  <Route path="/checkout/comercial/:token" element={<CheckoutComercial />} />
                  <Route path="/.lovable/oauth/consent" element={<OAuthConsent />} />
                  {/* Ferramentas gratuitas públicas (sem login) */}
                  <Route path="/ferramentas" element={<ToolsHub />} />
                  <Route path="/ferramentas/sorteio-de-duplas" element={<PairDrawTool />} />
                  <Route path="/ferramentas/chaveamento" element={<BracketTool />} />
                  <Route path="/ferramentas/ao-vivo/:code" element={<PublicDrawLive />} />
                  {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}

                  {/* Compat: rotas antigas /perfil/:username redirecionam para a URL raiz /:username */}
                  <Route path="/perfil/:username" element={<LegacyProfileRedirect />} />
                  {/* Catch-all de handles públicos (atletas, times, arenas) na URL raiz `/diegodavello`, `/tubaroes-fc`. */}
                  <Route path="/:handle" element={<HandleResolver />} />
                  <Route path="*" element={<NotFound />} />
                </Routes>
              </Suspense>
            </Layout>
          </AuthProvider>
        </BrowserRouter>
      </TooltipProvider>
    </ThemeProvider>
  </QueryClientProvider>
  </HelmetProvider>
);

// Redirect compat: /perfil/:username -> /:username
function LegacyProfileRedirect() {
  const username = window.location.pathname.split('/').filter(Boolean)[1];
  return <Navigate to={`/${username ?? ''}`} replace />;
}

export default App;
